import { useEffect } from 'react';
import { create } from 'zustand';
import { useCurrentUser } from '@/lib/useCurrentUser';
import type { SearchResourceTypeUi } from '@/features/search/searchMeta';

/**
 * „Zuletzt geöffnet" der mobilen Navigation (`MobileDock`, 2026-09-24).
 *
 * Ersetzt die festen Plätze der früheren Tab-Leiste: Wer den ganzen Tag in zwei
 * Projekten arbeitet, hat genau diese zwei vorne. Gemerkt werden Module (Seiten
 * aus der Navigation) und Datensätze (Suchtreffer, Projekt-Detailseite).
 *
 * Pro Gerät im localStorage und je Benutzer getrennt (`userId`) — ein geteiltes
 * Werkstatt-Tablet zeigt nicht den Verlauf der vorigen Person. Serverseitig
 * gespeichert wird bewusst nichts: Das ist eine Bequemlichkeit, keine Datenhaltung.
 */
export interface RecentPlace {
  path: string;
  label: string;
  /** Zweite Zeile, z. B. „Projekt" oder der Bereich eines Moduls. */
  detail: string;
  /** Datensatz-Typ (für das Symbol); fehlt bei Modulen. */
  resourceType?: SearchResourceTypeUi;
  userId: string;
  visitedAt: number;
}

export const RECENT_PLACES_KEY = 'proerp:recent-places';
export const RECENT_PLACES_LIMIT = 8;

function load(): RecentPlace[] {
  try {
    const raw = localStorage.getItem(RECENT_PLACES_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is RecentPlace =>
        typeof entry === 'object' && entry !== null && typeof entry.path === 'string' && typeof entry.label === 'string' && typeof entry.userId === 'string',
    );
  } catch {
    return [];
  }
}

function store(places: RecentPlace[]): void {
  try {
    localStorage.setItem(RECENT_PLACES_KEY, JSON.stringify(places));
  } catch {
    // Best effort — ohne Storage lebt der Verlauf nur bis zum Neuladen.
  }
}

/**
 * Neuen Besuch vorne einreihen: dieselbe Adresse desselben Benutzers wird
 * ersetzt statt doppelt geführt, je Benutzer höchstens `RECENT_PLACES_LIMIT`.
 * Reine Funktion, damit sie ohne Store testbar ist.
 */
export function addRecentPlace(places: RecentPlace[], place: RecentPlace): RecentPlace[] {
  const others = places.filter((entry) => !(entry.userId === place.userId && entry.path === place.path));
  const mine = others.filter((entry) => entry.userId === place.userId).slice(0, RECENT_PLACES_LIMIT - 1);
  const foreign = others.filter((entry) => entry.userId !== place.userId);
  return [place, ...mine, ...foreign];
}

interface RecentPlacesState {
  places: RecentPlace[];
  remember: (place: Omit<RecentPlace, 'visitedAt'>) => void;
}

export const useRecentPlaces = create<RecentPlacesState>((set, get) => ({
  places: load(),
  remember: (place) => {
    const current = get().places;
    const head = current.find((entry) => entry.userId === place.userId);
    // Gleicher Ort, gleiche Beschriftung → nichts zu tun (vermeidet Render-Schleifen).
    if (head && head.path === place.path && head.label === place.label) return;
    const next = addRecentPlace(current, { ...place, visitedAt: Date.now() });
    store(next);
    set({ places: next });
  },
}));

/** Verlauf eines Benutzers, neueste zuerst. */
export function recentPlacesFor(places: RecentPlace[], userId: string | undefined): RecentPlace[] {
  if (!userId) return [];
  return places.filter((entry) => entry.userId === userId);
}

/**
 * Einen Datensatz für „Zuletzt geöffnet" merken, sobald seine Seite geladen ist
 * (`place` null = noch nicht geladen). Bewusst nur auf den Detailseiten selbst
 * aufrufen, nicht in eingebetteten Ansichten (Teambesprechung blättert durch
 * viele Projekte — das ist kein „geöffnet").
 */
export function useRememberRecentPlace(place: Omit<RecentPlace, 'visitedAt' | 'userId'> | null): void {
  const userId = useCurrentUser().data?.data.id;
  const remember = useRecentPlaces((state) => state.remember);
  const path = place?.path;
  const label = place?.label;
  const detail = place?.detail;
  const resourceType = place?.resourceType;
  useEffect(() => {
    if (!userId || !path || !label) return;
    remember({ path, label, detail: detail ?? '', resourceType, userId });
  }, [userId, path, label, detail, resourceType, remember]);
}
